import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ApiService,
  AppInsightService,
  KioskToken,
  LobbyType,
  LogEvents,
  LynxLoginInfo,
  StorageService,
  StorageType,
  TimerService
} from '@rct/lynx-lobbies-shared-lib';
import dayjs from 'dayjs';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, tap } from 'rxjs/operators';
import { LYNX_STORAGE_KEYS } from '../constants/lynx-storage-keys.constant';
import { LynxTokenConstant } from '../constants/token-constant';

@Injectable({
  providedIn: 'root'
})
export class LynxAuthService {
  private isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    undefined
  );
  private isForceLogoutSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  private apiService = inject(ApiService);
  private storageService = inject(StorageService);
  private timerService = inject(TimerService);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private appInsightService = inject(AppInsightService);

  get isLoggedIn$(): Observable<boolean> {
    return this.isLoggedInSubject.pipe(filter((x) => x !== undefined));
  }

  get isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  get isForceLogout$(): Observable<boolean> {
    return this.isForceLogoutSubject.asObservable();
  }

  forceLogout(): void {
    this.isForceLogoutSubject.next(true);
    this.isLoggedInSubject.next(false);
    this.removeAccount();
  }

  checkAuthentication(): void {
    if (
      this.storageService.get(LYNX_STORAGE_KEYS.kioskToken, StorageType.Session)
    ) {
      this.isLoggedInSubject.next(true);
    }
  }

  login(account: string, password: string, isRememberMe: boolean): Observable<LynxLoginInfo> {
    const tokenLoginSource = this.apiService.tokenLogin$(
      this.getRememberMeToken()
    );
    const loginSource = of(null).pipe(
      concatMap(() =>
        this.recaptchaV3Service.execute('importantAction').pipe(
          catchError((error) => {
            return throwError(new HttpErrorResponse({ error, status: 500 }));
          })
        )
      ),
      concatMap((recaptcha) =>
        this.apiService.login$(account, password, isRememberMe, recaptcha)
      )
    );
    const source = this.isTokenLogin(account, password, isRememberMe)
      ? tokenLoginSource
      : loginSource;
    return source.pipe(
      map((data) => <LynxLoginInfo>data),
      tap((res) => {
        this.storageService.set(LYNX_STORAGE_KEYS.user, { userName: account });

        if (isRememberMe) {
          // replace password with same size *
          const replacedPassword = '*'.repeat(password.length);
          // save kioskToken to local storage
          this.setAccount(account, replacedPassword, res.kioskToken);
        }
        this.setKioskToken(
          res.kioskToken,
          res.kioskTokenIssueTime, // dayjs().toISOString(), -> For testing only
          res.kioskTokenExpiryInSeconds
        );
        this.isLoggedInSubject.next(true);
      })
    );
  }

  private getRememberMeToken(): string | null {
    const data = this.getAccount();
    if (data && data.rememberMeToken) {
      return data.rememberMeToken;
    }
    return null;
  }

  private isTokenLogin(
    account: string,
    password: string,
    isRememberMe: boolean
  ): boolean {
    const data = this.getAccount();
    if (isRememberMe && data) {
      return `${account}${password}` === `${data.account}${data.password}`;
    }
    return false;
  }

  logout(): Observable<any> {
    return this.apiService.logout$().pipe(
      tap(() => {
        this.appInsightService.logEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.Logout
        );
        this.appInsightService.gaEvent(
          { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
          LogEvents.Logout
        );
      }),
      tap((_) => {
        this.isLoggedInSubject.next(false);
        this.storageService.lynxClearExceptLobbyAndLocale(LYNX_STORAGE_KEYS);
      })
    );
  }

  initKioskTimer(): void {
    const kioskObj = this.storageService.get(
      LYNX_STORAGE_KEYS.kioskToken,
      StorageType.Session
    );
    const rememberMeToken = this.getRememberMeToken();
    if (kioskObj && !rememberMeToken) {
      this.setKioskTimer(
        kioskObj.kioskTokenIssueTime,
        kioskObj.kioskTokenExpiryInSeconds,
        LynxTokenConstant.kioskToken.refreshPercentage,
        LynxTokenConstant.kioskToken.retryPercentages
      );
    }
  }

  refreshKioskToken(
    lastTokenIssueTime: string,
    lastTokenExpiryInSeconds: number,
    retryOptions: number[]
  ): Observable<KioskToken> {
    const now = dayjs().toISOString();
    console.log(
      'RefreshKioskToken at:',
      now,
      'Retry times left:',
      retryOptions.length
    );

    return this.apiService.refreshToken$().pipe(
      map((data) => <KioskToken>data),
      tap((res) => {
        this.setKioskTimer(
          res.kioskTokenIssueTime, // now, -> For testing only
          res.kioskTokenExpiryInSeconds,
          LynxTokenConstant.kioskToken.refreshPercentage,
          LynxTokenConstant.kioskToken.retryPercentages
        );
        this.setKioskToken(
          res.kioskToken,
          res.kioskTokenIssueTime, // now, -> For testing only
          res.kioskTokenExpiryInSeconds
        );
      }),
      catchError((error) => {
        if (retryOptions.length > 0) {
          const retryPercentage = retryOptions.splice(0, 1)[0];
          this.setKioskTimer(
            lastTokenIssueTime,
            lastTokenExpiryInSeconds,
            retryPercentage,
            retryOptions
          );
        } else {
          this.forceLogout();
        }

        return of(error);
      })
    );
  }

  private setKioskTimer(
    issueTime: string,
    expiryInSeconds: number,
    refreshPercentage: number,
    retryOptions: number[]
  ): void {
    const now = dayjs();
    const refreshTime = dayjs(issueTime).add(
      expiryInSeconds * refreshPercentage,
      'second'
    );
    if (refreshTime.isAfter(now)) {
      const refreshInSeconds = refreshTime.diff(now, 'second');
      this.timerService.start({
        action: this.refreshKioskToken.bind(
          this,
          issueTime,
          expiryInSeconds,
          retryOptions
        ),
        startInSeconds: refreshInSeconds
      });
    }
  }

  private setKioskToken(
    token: string,
    issueTime: string,
    expiryInSeconds: number
  ): void {
    const actualExpireInSeconds = dayjs(issueTime)
      .add(expiryInSeconds, 'second')
      .diff(dayjs(), 'second');
    this.storageService.set(
      LYNX_STORAGE_KEYS.kioskToken,
      {
        kioskToken: token,
        kioskTokenIssueTime: issueTime,
        kioskTokenExpiryInSeconds: expiryInSeconds
      },
      {
        type: StorageType.Session,
        expiredAt: actualExpireInSeconds,
        expiredUnit: 's'
      }
    );
  }

  setAccount(account: string, password: string, rememberMeToken: string): void {
    const lobbyId = this.storageService.get(
      LYNX_STORAGE_KEYS.lobbyId,
      StorageType.Session
    );
    const data =
      this.storageService.get(LYNX_STORAGE_KEYS.account, StorageType.Local) ||
      {};
    data[lobbyId] = { account, password, rememberMeToken };
    this.storageService.set(LYNX_STORAGE_KEYS.account, data, {
      type: StorageType.Local
    });
  }

  getAccount(): any {
    const lobbyId = this.storageService.get(
      LYNX_STORAGE_KEYS.lobbyId,
      StorageType.Session
    );
    const data =
      this.storageService.get(LYNX_STORAGE_KEYS.account, StorageType.Local) ||
      {};
    return data[lobbyId];
  }

  removeAccount(): void {
    const lobbyId = this.storageService.get(
      LYNX_STORAGE_KEYS.lobbyId,
      StorageType.Session
    );
    const data =
      this.storageService.get(LYNX_STORAGE_KEYS.account, StorageType.Local) ||
      null;
    if (data) {
      delete data[lobbyId];
      this.storageService.set(LYNX_STORAGE_KEYS.account, data, {
        type: StorageType.Local
      });
    }
  }
}
